import React, { useState, useEffect } from "react";
import { Button } from "antd";
import styled from "styled-components";
import { TableLayoutData, DetailFields } from "../../styledElements";
import {
  lengthUnit,
  weightUnit,
} from "../../../Utils/Constant/MeasurementUnitList";

const CustomBackground = styled.div`
  width: 100%;
  overflow-x: hidden;
  overflow-y: visible;
  padding-bottom: 12px;

  .table-wrapper {
    display: inline-block;
    width: 100%;
  }

  .grid-container {
    display: grid;
    width: 100%;
    border-bottom: 1px solid #f2c2c0;
  }

  .grid-header,
  .grid-cell {
    word-break: break-word;
    white-space: normal;
  }

  .grid-header {
    background-color: #fbe0de;
    text-align: center;
    padding: 10px 8px;
    font-weight: 600;
    font-size: 15px;
    border-right: 1px solid #f2c2c0;
  }

  .param-col {
    background-color: #fbe0de;
    text-align: left;
    padding-left: 10px;
  }

  .grid-cell {
    background-color: #fff;
    padding: 10px 12px;
    text-align: center;
    font-size: 14px;
    border-right: 1px solid #f2c2c0;
  }
`;

const PARAMETERS = [
  "Trim Loss %",
  "Net Trim Loss % (Incl. Edge Trim)",
  "Fulfillment (%)",
  "Trim Weight",
  "Avg. Deckle Utilized",
  "Planned Order Weight",
  "Suggested Production Weight",
  "Excess %",
  "Shortfall %",
  "Stock Weight (Suggested/ Req.)",
  "Unique Patterns",
  "Total Sets",
  "Total Orders",
  "Order Fully Fulfilled",
  "Orders Partially Fulfilled",
  "Completely Unfulfilled Orders",
];

export default function CreateNewSolCompare({
  newSummaryData,
  summary,
  onAccept,
  setSolutionId,
  jobIndex,
  isMultistage = false,
}) {
  const [dataSource, setDataSource] = useState({});

  useEffect(() => {
    console.log("newSummaryData", newSummaryData);
    console.log("summary", summary);
    console.log("jobIndex", jobIndex);
    if (jobIndex === -1) return;
    const solution1Summary =
      summary?.solution1?.[jobIndex]?.jobcard?.solution_1?.data?.summary;

    const solution2Summary =
      summary?.solution2?.[jobIndex]?.jobcard?.solution_2?.data?.summary;

    // if (!solution1Summary && !solution2Summary) return;
    const emptyParams = PARAMETERS.reduce((acc, param) => {
      acc[param] = 0;
      return acc;
    }, {});

    // helper function to map your summary data to the display parameters
    const mapSummaryToParams = (summarySet, job) => {
      // if (!summarySet) return { ...emptyParams };

      const machineId = summarySet?.group?.machines?.[0]?.machine_id;
      const multistageRewinderMachineId =
        summarySet?.group?.machines?.[0]?.rewinder_id || null;
      const deckleUnitKey =
        summarySet?.group?.machines?.[0]?.units?.deckle_size_ub;
      const deckleDisplayName =
        lengthUnit?.[deckleUnitKey]?.displayName?.toUpperCase() ?? "";

      // safely access avg_utilized_deckle key
      console.log(summarySet, job);

      const avgDeckleValue =
        summarySet?.[job]?.data?.summary?.[
          `avg_utilized_deckle_${machineId}`
        ]?.toFixed?.(3) ?? 0;
      const avg_utilized_deckle_without_trim =
        summarySet?.[job]?.data?.summary?.[
          `avg_utilized_deckle_without_trim_${multistageRewinderMachineId}`
        ];

      const jobcard = summarySet?.[job]?.data?.summary;

      return {
        ...emptyParams,

        "Trim Loss %": `${(jobcard?.match_trim_loss_percentage ?? 0).toFixed(3)}%`,
        "Net Trim Loss % (Incl. Edge Trim)": `${(jobcard?.net_trim_loss_percentage ?? 0).toFixed(3)}%`,
        "Fulfillment (%)": `${(jobcard?.requirement_fulfilled_percentage ?? 0).toFixed(3)}%`,

        "Trim Weight": `${jobcard?.total_net_trim_loss ?? 0}${
          weightUnit?.[
            jobcard?.units?.total_net_trim_loss
          ]?.displayName?.toUpperCase() ?? ""
        }`,

        "Avg. Deckle Utilized": `${
          isMultistage
            ? (avg_utilized_deckle_without_trim ?? 0)
            : (avgDeckleValue ?? 0)
        } ${deckleDisplayName ?? "CM"}`,

        "Planned Order Weight": `${(
          (jobcard?.total_weight_requirement ?? 0) -
          (jobcard?.stock_weight_requirement ?? 0)
        ).toFixed(3)}${
          weightUnit?.[
            jobcard?.units?.total_weight_requirement
          ]?.displayName?.toUpperCase() ?? ""
        }`,

        "Suggested Production Weight": `${jobcard?.job_card_weight ?? 0}${
          weightUnit?.[
            jobcard?.units?.job_card_weight
          ]?.displayName?.toUpperCase() ?? ""
        }`,

        "Excess %": jobcard?.fulfilled_requirement_weight
          ? `${(
              ((jobcard?.excess_orders_weight ?? 0) /
                ((jobcard?.total_weight_requirement ?? 0) -
                  (jobcard?.stock_weight_requirement ?? 0))) *
              100
            ).toFixed(2)}%`
          : "0%",

        "Shortfall %": jobcard?.fulfilled_requirement_weight
          ? `${(
              ((jobcard?.shortfall_orders_weight ?? 0) /
                (jobcard?.fulfilled_requirement_weight ?? 1)) *
              100
            ).toFixed(2)}%`
          : "0%",

        "Stock Weight (Suggested/ Req.)": `${
          jobcard?.stock_requirement_fulfilled ?? 0
        }${
          weightUnit?.[
            jobcard?.units?.stock_requirement_fulfilled
          ]?.displayName?.toUpperCase() ?? ""
        }/${jobcard?.stock_weight_requirement ?? 0}${
          weightUnit?.[
            jobcard?.units?.stock_weight_requirement
          ]?.displayName?.toUpperCase() ?? ""
        }`,

        "Unique Patterns":
          jobcard?.unique_partitions ??
          `${jobcard?.unique_rewinding_partitions ?? 0}+${
            jobcard?.unique_cutting_patterns ?? 0
          }`,

        "Total Sets":
          jobcard?.total_partitions ?? jobcard?.total_rewinding_partitions ?? 0,

        "Total Orders": jobcard?.total_orders ?? 0,
        "Order Fully Fulfilled": jobcard?.orders_fully_fulfilled ?? 0,
        "Orders Partially Fulfilled": jobcard?.orders_partially_fulfilled ?? 0,
        "Completely Unfulfilled Orders":
          jobcard?.orders_completely_unfulfilled ?? 0,
      };
    };

    const ds = {
      solution1: mapSummaryToParams(
        summary?.solution1?.[jobIndex]?.jobcard,
        "solution_1",
      ),
      solution2: mapSummaryToParams(
        summary?.solution2?.[jobIndex]?.jobcard,
        "solution_2",
      ),
    };

    if (
      newSummaryData &&
      (Object.keys(
        newSummaryData.solution1?.[jobIndex]?.newlyCreatedJobCardsDetails || {},
      ).length > 0 ||
        Object.keys(
          newSummaryData.solution2?.[jobIndex]?.newlyCreatedJobCardsDetails ||
            {},
        ).length > 0)
    ) {
      ds.solution3 = mapSummaryToParams(
        newSummaryData?.solution1[jobIndex]?.newlyCreatedJobCardsDetails[0]
          ?.jobcard,
        "solution_1",
      );
      ds.solution4 = mapSummaryToParams(
        newSummaryData?.solution2[jobIndex]?.newlyCreatedJobCardsDetails[0]
          ?.jobcard,
        "solution_2",
      );
    }

    setDataSource(ds);
  }, [summary, newSummaryData, jobIndex]);

  const solutions = Object.keys(dataSource).filter((key) => {
    // Hide solution3 & solution4 if newSummaryData is not provided
    if (!newSummaryData && (key === "solution3" || key === "solution4"))
      return false;
    return true;
  });

  const handleAccept = (solutionKey, index) => {
    console.log(`Accepted ${solutionKey}`);
    setSolutionId(index.toString());
    let solID =
      solutionKey === "solution1" || "solution3" ? "solution1" : "solution2";
    onAccept(solID);
  };

  // Responsive column widths:
  // Parameters column fixed width
  const paramColWidth = 300;

  // Calculate solution column widths dynamically:
  // For 2 columns, fill the rest of width equally
  // For 3 or 4, fixed max width per column (e.g., 280px)
  const totalWidth = 1200; // max total width before scroll triggers, can adjust or make dynamic
  let solColWidth;

  if (solutions.length === 2) {
    solColWidth = `calc((80vw - ${paramColWidth}px) / 2)`;
  } else {
    solColWidth = "230px";
  }

  // Build gridTemplateColumns string dynamically
  // const gridTemplateColumns =
  //   solutions.length === 2
  //     ? `${paramColWidth}px ${solColWidth} ${solColWidth}`
  //     : `${paramColWidth}px repeat(${solutions.length}, ${solColWidth})`;
  // Responsive column widths:
  const gridTemplateColumns = `250px repeat(${solutions.length}, 1fr)`;

  return (
    <CustomBackground>
      <div className="table-wrapper">
        <DetailFields>
          <TableLayoutData>
            <div className="tableDataFormat" style={{ minWidth: "100%" }}>
              {/* Header Row */}
              <div
                className="grid-container"
                style={{
                  gridTemplateColumns,
                  borderTop: "3px solid var(--theme-color)",
                }}
              >
                <div className="grid-header param-col">Parameters</div>
                {solutions.map((solution, index) => (
                  <div key={index} className="grid-header">
                    <div>{`Solution ${index + 1}`}</div>

                    {/* Sol 1 Accept Button */}
                    {solution === "solution1" ? (
                      summary?.[solution]?.[jobIndex]?.jobcard?.solution_1
                        ?.status === "accept" ? (
                        <span
                          style={{
                            color: "green",
                            fontSize: "12px",
                            marginTop: "6px",
                          }}
                        >
                          Accepted
                        </span>
                      ) : summary?.[solution]?.[jobIndex]?.jobcard?.solution_1
                          ?.status === "reject" ? (
                        <span
                          style={{
                            color: "red",
                            fontSize: "12px",
                            marginTop: "6px",
                          }}
                        >
                          Rejected
                        </span>
                      ) : summary?.[solution]?.[jobIndex]?.jobcard?.solution_1
                          ?.status === "open" &&
                        Object.keys(
                          summary?.[solution]?.[jobIndex]?.jobcard?.solution_1
                            ?.data?.summary,
                        ).length > 0 ? (
                        <Button
                          style={{ marginTop: "6px" }}
                          className="themeBtn"
                          onClick={() => handleAccept(solution, index + 1)}
                        >
                          Accept
                        </Button>
                      ) : (
                        ""
                      )
                    ) : null}

                    {solution === "solution2" ? (
                      summary?.[solution]?.[jobIndex]?.jobcard?.solution_2
                        ?.status === "accept" ? (
                        <span
                          style={{
                            color: "green",
                            fontSize: "12px",
                            marginTop: "6px",
                          }}
                        >
                          Accepted
                        </span>
                      ) : summary?.[solution]?.[jobIndex]?.jobcard?.solution_2
                          ?.status === "reject" ? (
                        <span
                          style={{
                            color: "red",
                            fontSize: "12px",
                            marginTop: "6px",
                          }}
                        >
                          Rejected
                        </span>
                      ) : summary?.[solution]?.[jobIndex]?.jobcard?.solution_2
                          ?.status === "open" &&
                        Object.keys(
                          summary?.[solution]?.[jobIndex]?.jobcard?.solution_2
                            ?.data?.summary,
                        ).length > 0 ? (
                        <Button
                          style={{ marginTop: "6px" }}
                          className="themeBtn"
                          onClick={() => handleAccept(solution, index + 1)}
                        >
                          Accept
                        </Button>
                      ) : (
                        ""
                      )
                    ) : null}

                    {solution === "solution3" ? (
                      newSummaryData?.[
                        solution === "solution3" ? "solution1" : "solution2"
                      ]?.[jobIndex]?.newlyCreatedJobCardsDetails?.[0]?.jobcard
                        ?.solution_1?.status === "accept" ? (
                        <span
                          style={{
                            color: "green",
                            fontSize: "12px",
                            marginTop: "6px",
                          }}
                        >
                          Accepted
                        </span>
                      ) : newSummaryData?.[
                          solution === "solution3" ? "solution1" : "solution2"
                        ]?.[jobIndex]?.newlyCreatedJobCardsDetails?.[0]?.jobcard
                          ?.solution_1?.status === "reject" ? (
                        <span
                          style={{
                            color: "red",
                            fontSize: "12px",
                            marginTop: "6px",
                          }}
                        >
                          Rejected
                        </span>
                      ) : newSummaryData?.[
                          solution === "solution3" ? "solution1" : "solution2"
                        ]?.[jobIndex]?.newlyCreatedJobCardsDetails?.[0]?.jobcard
                          ?.solution_1?.status === "open" &&
                        Object.keys(
                          newSummaryData?.[
                            solution === "solution3" ? "solution1" : "solution2"
                          ]?.[jobIndex]?.newlyCreatedJobCardsDetails?.[0]
                            ?.jobcard?.solution_1?.data?.summary,
                        ).length > 0 ? (
                        <Button
                          style={{ marginTop: "6px" }}
                          className="themeBtn"
                          onClick={() => handleAccept(solution, index + 1)}
                        >
                          Accept
                        </Button>
                      ) : (
                        ""
                      )
                    ) : null}

                    {solution === "solution4" ? (
                      newSummaryData?.[
                        solution === "solution4" ? "solution2" : "solution1"
                      ]?.[jobIndex]?.newlyCreatedJobCardsDetails?.[0]?.jobcard
                        ?.solution_2?.status === "accept" ? (
                        <span
                          style={{
                            color: "green",
                            fontSize: "12px",
                            marginTop: "6px",
                          }}
                        >
                          Accepted
                        </span>
                      ) : newSummaryData?.[
                          solution === "solution4" ? "solution2" : "solution1"
                        ]?.[jobIndex]?.newlyCreatedJobCardsDetails?.[0]?.jobcard
                          ?.solution_2?.status === "reject" ? (
                        <span
                          style={{
                            color: "red",
                            fontSize: "12px",
                            marginTop: "6px",
                          }}
                        >
                          Rejected
                        </span>
                      ) : newSummaryData?.[
                          solution === "solution4" ? "solution2" : "solution1"
                        ]?.[jobIndex]?.newlyCreatedJobCardsDetails?.[0]?.jobcard
                          ?.solution_2?.status === "open" &&
                        Object.keys(
                          newSummaryData?.[
                            solution === "solution4" ? "solution2" : "solution1"
                          ]?.[jobIndex]?.newlyCreatedJobCardsDetails?.[0]
                            ?.jobcard?.solution_2?.data?.summary,
                        ).length > 0 ? (
                        <Button
                          style={{ marginTop: "6px" }}
                          className="themeBtn"
                          onClick={() => handleAccept(solution, index + 1)}
                        >
                          Accept
                        </Button>
                      ) : (
                        ""
                      )
                    ) : null}
                  </div>
                ))}
              </div>

              {/* Parameter Rows */}
              {PARAMETERS.map((param, rowIndex) => (
                <div
                  key={rowIndex}
                  className="grid-container"
                  style={{
                    gridTemplateColumns,
                  }}
                >
                  <div className="grid-header param-col">{param}</div>
                  {solutions.map((solution, colIndex) => (
                    <div key={colIndex} className="grid-cell">
                      {dataSource[solution][param]}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </TableLayoutData>
        </DetailFields>
      </div>
    </CustomBackground>
  );
}
